import { formatDate } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, inject, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { AccountMarketplaceService, AccountSelectionService } from "@front/m19-services";
import {
  IButtonDirective,
  ITooltipDirective,
  IDatepickerComponent,
  IDateRange,
  MODAL_DATA,
  ModalRef,
  ToasterService,
} from "@front/m19-ui";
import { DateUtils } from "@front/m19-utils";
import { TranslocoService } from "@jsverse/transloco";
import { DateTime } from "luxon";

export interface PromoDaysBoostDateRangeModalData {
  dateRange: IDateRange;
  minDate: DateTime;
  maxDate: DateTime;
  locale: string;
  isEdit?: boolean;
}

@Component({
  selector: "app-promo-days-boost-date-range-modal",
  imports: [IDatepickerComponent, IButtonDirective, ITooltipDirective],
  templateUrl: "./promo-days-boost-date-range-modal.component.html",
  styles: ``,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PromoDaysBoostDateRangeModalComponent {
  readonly accountSelectionService = inject(AccountSelectionService);
  readonly accountMarketplaceService = inject(AccountMarketplaceService);
  readonly toasterService = inject(ToasterService);
  readonly translocoService = inject(TranslocoService);

  readonly data = inject<PromoDaysBoostDateRangeModalData>(MODAL_DATA);
  readonly modalRef = inject(ModalRef);

  readonly accountMarketplace = toSignal(this.accountSelectionService.singleAccountMarketplaceSelection$, {
    requireSync: true,
  });

  dateRange = signal<IDateRange>(this.data.dateRange);
  readonly dateFormat = computed(() => DateUtils.getDateFormatString(this.data.locale));
  readonly startDate = computed(() => formatDate(this.dateRange().start.toJSDate(), "mediumDate", this.data.locale));
  readonly endDate = computed(() => {
    const end = this.dateRange().end;
    return end ? formatDate(end.toJSDate(), "mediumDate", this.data.locale) : "-";
  });
  readonly isActivateButtonDisabled = computed(() => !this.dateRange().start || !this.dateRange().end);
  readonly activateButtonTooltip = computed(() =>
    this.isActivateButtonDisabled() ? "Please select a valid date range" : undefined,
  );

  activatePromoDays() {
    if (this.isActivateButtonDisabled()) return;

    this.modalRef.close(this.dateRange());
  }
}
